<template>
    <BaseModal
        :title="isEditing ? 'Edit Role' : 'Add Role'"
        :loading="loading"
        submit-text="Save"
        max-width="4xl"
        @close="closeModal"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit" class="space-y-6">
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Role Information
                </h4>
                <div class="grid grid-cols-1 gap-4">
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Role Name *
                        </label>
                        <input
                            v-model="form.name"
                            type="text"
                            required
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.name,
                            }"
                            placeholder="Enter role name"
                        />
                        <p
                            v-if="errors.name && errors.name.length > 0"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.name[0] }}
                        </p>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Guard *
                        </label>
                        <select
                            v-model="form.guard_name"
                            required
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.guard_name,
                            }"
                        >
                            <option value="web">Web</option>
                            <option value="api">API</option>
                        </select>
                        <p
                            v-if="
                                errors.guard_name &&
                                errors.guard_name.length > 0
                            "
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.guard_name[0] }}
                        </p>
                    </div>
                </div>
            </div>

            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Permissions
                </h4>
                <div class="mb-3">
                    <div class="relative">
                        <input
                            v-model="searchQuery"
                            type="text"
                            placeholder="Search permissions..."
                            class="w-full px-3 py-2 pl-9 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                        />
                        <Search
                            class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                        />
                    </div>
                </div>
                <div
                    class="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700"
                >
                    <template v-if="filteredGroupedPermissions.length > 0">
                        <div
                            v-for="permission in filteredGroupedPermissions"
                            :key="permission.group"
                            class="space-y-2"
                        >
                            <h4
                                class="text-sm font-medium text-gray-900 dark:text-white capitalize"
                            >
                                {{ permission.group }}
                            </h4>
                            <div class="space-y-1">
                                <label
                                    v-for="perm in permission.items"
                                    :key="perm.id"
                                    class="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded"
                                >
                                    <input
                                        type="checkbox"
                                        :value="perm.id"
                                        v-model="form.permissions"
                                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span
                                        class="text-sm text-gray-700 dark:text-gray-300"
                                        >{{ perm.name }}</span
                                    >
                                </label>
                            </div>
                        </div>
                    </template>
                    <div
                        v-else
                        class="col-span-2 text-center py-8 text-sm text-gray-500 dark:text-gray-400"
                    >
                        No permissions found matching your search.
                    </div>
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import { Search } from "@lucide/vue";
import type { Role, Permission } from "@/api/role";
import { useRoleStore } from "@/stores/role";
import BaseModal from "@/components/BaseModal.vue";
import permissionApi from "@/api/permission";

interface Props {
    currentRole?: Role | null;
    editMode: boolean;
    isOpen?: boolean;
}

interface Emits {
    close: [];
    saved: [];
}

const props = withDefaults(defineProps<Props>(), {
    currentRole: null,
    isOpen: false,
});

const emit = defineEmits<Emits>();

const roleStore = useRoleStore();

const isEditing = computed(() => props.editMode);

const loading = ref(false);
const errors = ref<Record<string, string[]>>({});
const searchQuery = ref("");
const allPermissions = ref<Permission[]>([]);

const form = ref({
    name: "",
    guard_name: "web",
    permissions: [] as number[],
});

const groupedPermissions = computed(() => {
    const grouped = allPermissions.value.reduce(
        (acc, permission) => {
            const group = permission.group_name || "general";
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(permission);
            return acc;
        },
        {} as Record<string, Permission[]>,
    );

    return Object.entries(grouped)
        .map(([group, items]) => ({
            group,
            items: items.sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => {
            if (a.group === "general") return -1;
            if (b.group === "general") return 1;
            return a.group.localeCompare(b.group);
        });
});

const filteredGroupedPermissions = computed(() => {
    if (!searchQuery.value.trim()) {
        return groupedPermissions.value;
    }

    const query = searchQuery.value.toLowerCase();

    return groupedPermissions.value
        .map((group) => ({
            ...group,
            items: group.items.filter((perm) =>
                perm.name.toLowerCase().includes(query),
            ),
        }))
        .filter((group) => group.items.length > 0);
});

const fetchPermissions = async () => {
    try {
        const response = await permissionApi.getAllPermissions();
        allPermissions.value = response.data || [];
    } catch (error) {
        console.error("Failed to fetch permissions:", error);
    }
};

const resetForm = () => {
    form.value = {
        name: "",
        guard_name: "web",
        permissions: [],
    };
    resetErrors();
};

const resetErrors = () => {
    errors.value = {};
};

watch(
    () => props.currentRole,
    (newRole: Role | null) => {
        resetErrors();
        if (newRole && props.editMode) {
            form.value = {
                name: newRole.name || "",
                guard_name: newRole.guard_name || "web",
                permissions:
                    newRole.permissions?.map((p: Permission) => p.id) || [],
            };
        } else {
            resetForm();
        }
    },
    { immediate: true },
);

watch(
    () => props.isOpen,
    () => {
        resetErrors();
    },
);

const validateForm = () => {
    const newErrors: Record<string, string[]> = {};

    if (!form.value.name.trim()) {
        newErrors.name = ["Role name is required"];
    }

    if (!form.value.guard_name.trim()) {
        newErrors.guard_name = ["Guard is required"];
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const closeModal = () => {
    emit("close");
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;
    resetErrors();
    try {
        if (isEditing.value && props.currentRole) {
            await roleStore.updateRole(props.currentRole.id, form.value);
        } else {
            await roleStore.createRole(form.value);
        }
        resetForm();
        errors.value = {};
        emit("saved");
        emit("close");
    } catch (error: any) {
        console.error("Save error:", error);

        if (error.response?.status === 422) {
            errors.value = error.response.data.errors;
            return;
        }
        throw error;
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchPermissions();
});
</script>
